/* eslint-disable max-classes-per-file */

import { CONCEPTS } from '@rover/rsdk/src/concepts';
import type { ConsentPurpose } from '@rover/rsdk/src/modules/Analytics';
import { AnalyticsEvent } from '@rover/types/src/Analytics';

/**
 * Maps a wizard step index to a human-readable name used in analytics payloads.
 */
export const STEP_NAMES: Record<number, string> = {
  0: 'service-details',
  1: 'pet-count',
  2: 'pet-information',
};

export const getStepName = (step: number): string => STEP_NAMES[step];

/** Common payload shared by every wizard analytics event. */
type StepPayload = {
  stepName: string;
};

/**
 * &nbsp;
 *
 * # Description
 *
 * Fires on initial mount of the SearchWizardModal and on every step change.
 * Used to measure funnel progression through the modal's steps.
 *
 * Payload: `{ stepName }`
 *
 * &nbsp;
 */
export class SearchOwnerPreferencesStepViewEvent extends AnalyticsEvent {
  eventName = 'search-owner-preferences-step-view';

  consentPurpose: ConsentPurpose = 'functional';

  concept = CONCEPTS.recommendations.browse_search;

  constructor(eventProperties: StepPayload) {
    super(eventProperties, eventProperties);
  }
}

/**
 * &nbsp;
 *
 * # Description
 *
 * Fires on every non-submit close of the SearchWizardModal (X icon, overlay
 * click, escape). `persisted` indicates whether the user's changes were written
 * back to page-level search state on dismiss (true whenever past step 0).
 *
 * Payload: `{ stepName, persisted }`
 *
 * &nbsp;
 */
export class SearchOwnerPreferencesDismissEvent extends AnalyticsEvent {
  eventName = 'search-owner-preferences-dismiss';

  consentPurpose: ConsentPurpose = 'functional';

  concept = CONCEPTS.recommendations.browse_search;

  constructor(eventProperties: StepPayload & { persisted: boolean }) {
    super(eventProperties, eventProperties);
  }
}

/**
 * &nbsp;
 *
 * # Description
 *
 * Fires when the user clicks "Search Now" on the final step of the
 * SearchWizardModal, distinguishing an explicit submit from a dismiss.
 *
 * Payload: `{ stepName }`
 *
 * &nbsp;
 */
export class SearchOwnerPreferencesSearchNowEvent extends AnalyticsEvent {
  eventName = 'search-owner-preferences-search-now';

  consentPurpose: ConsentPurpose = 'functional';

  concept = CONCEPTS.recommendations.browse_search;

  constructor(eventProperties: StepPayload) {
    super(eventProperties, eventProperties);
  }
}

/**
 * &nbsp;
 *
 * # Description
 *
 * Fires at the commit boundary (the point at which changes are written back to
 * page-level search state and therefore persist), one event per changed field.
 * This captures the final persisted value the user actually settled on, without
 * spamming an event per transient onChange.
 *
 * Payload: `{ field, value, stepName }`
 *
 * &nbsp;
 */
export class SearchOwnerPreferencesFieldChangeEvent extends AnalyticsEvent {
  eventName = 'search-owner-preferences-field-change';

  consentPurpose: ConsentPurpose = 'functional';

  concept = CONCEPTS.recommendations.browse_search;

  constructor(eventProperties: StepPayload & { field: string; value: unknown }) {
    super(eventProperties, eventProperties);
  }
}
